import { useState, useEffect, Fragment } from "react";
import RHFieldWrapper from "./RHFieldWrapper";
import FormField from "components/common/RHEditableTable_v2/FormField";
import { PlusIcon, MinusIcon } from "assets/svgs";

export default function RepeaterField({
  label,
  name,
  value,
  disabled,
  onChange,
  required,
  sx,
  isFullWidthField,
  description,
  columns,
  parentDataKey,
  selectedItem,
  editedDetails,
  setEditedDetails,
  editDetail,
  sandbox,
  handleValidationError,
  rowKey,
  forceRemountKey,
  setForceRemountKey,
}) {
  const [localSelectedItem, setLocalSelectedItem] = useState();

  function addRow() {
    setLocalSelectedItem((item) => {
      // Ensure we have a columns array to iterate over
      const columnsArray = Array.isArray(columns) ? columns : [];
      const emptyRow = columnsArray.reduce((acc, col) => {
        acc[col.field] = "";
        return acc;
      }, {});

      const newObject = structuredClone(item);
      if (newObject && newObject[name]) {
        newObject[name].push(emptyRow);
      }
      return newObject;
    });
  }

  function deleteRow(rowIndex) {
    if (!editedDetails || !editedDetails[name]) return;
    
    const newObject = structuredClone(editedDetails);
    newObject[name].splice(rowIndex, 1);
    setEditedDetails(newObject);
    setForceRemountKey(Date.now());
  }

  function getFieldInfo(key) {
    if (!Array.isArray(columns)) return null;
    return columns.find((col) => col.field === key);
  }

  function sortColumnsByRowKey(a, b) {
    // Safety check for valid inputs
    if (!a || !b || !a.field || !b.field) return 0;
    
    // Sorts by placing the rowKey field first, then alphabetically by field name
    if (a.field === rowKey) return -1;
    if (b.field === rowKey) return 1;
    return a.field.localeCompare(b.field);
  }

  useEffect(() => {
    if (!value) {
      addRow();
    }
  }, [value]);

  useEffect(() => {
    // Don't want to submit empty rows when a new row is added, so we manage state internally on this component and pass it to the FormField to be managed as the source of truth there.
    setLocalSelectedItem(editedDetails);
  }, [editedDetails]);

  // Ensure we have the required data structures
  const columnsToRender = Array.isArray(columns) ? columns : [];
  const rowsToRender = localSelectedItem && localSelectedItem[name] ? localSelectedItem[name] : [];

  return (
    <RHFieldWrapper label={label} fullWidth={true} inactive={disabled} descripton={description}>
      <form>
        {rowsToRender.map((row, i) => {
          // Skip rendering if row is not defined
          if (!row) return null;
          
          return (
            <Fragment key={`${label}-${i}`}>
              <div className="flex justify-start flex-wrap items-center">
                {columnsToRender
                  .slice()
                  .sort(sortColumnsByRowKey)
                  .map((fieldDef, j) => {
                    // Skip if fieldDef is invalid
                    if (!fieldDef || !fieldDef.field) return null;
                    
                    // Ensure field exists in row data
                    if (!row.hasOwnProperty(fieldDef.field)) {
                      row[fieldDef.field] = fieldDef.required ? "" : null;
                    }
                    
                    return (
                      <div className="flex p-2 min-w-[350px]" key={`repeater-${fieldDef.field}-${forceRemountKey}`}>
                        <div className="flex w-full">
                          <FormField
                            selectedItem={localSelectedItem}
                            editedDetails={row}
                            setEditedDetails={setEditedDetails}
                            editDetail={editDetail}
                            field={fieldDef}
                            rowKey={fieldDef.field}
                            arrayKey={name}
                            sandbox={sandbox}
                            handleValidationError={handleValidationError}
                            customEditDetailArgs={{ 
                              rowIndex: i, 
                              rowKey: row[rowKey] || null 
                            }}
                            fullWidth={true}
                            parentRowKey={rowKey}
                          />
                        </div>
                      </div>
                    );
                  })}
                <div className={`flex items-center ml-4 ${disabled ? "pointer-events-none opacity-30" : ""}`}>
                  <div
                    className="flex items-center justify-center w-6 h-6 bg-white/10 rounded-full cursor-pointer mr-2 "
                    onClick={() => deleteRow(i, row[rowKey])}
                  >
                    <MinusIcon />
                  </div>
                  {i === rowsToRender.length - 1 && (
                    <div
                      className="flex items-center justify-center w-6 h-6 bg-white/10 rounded-full cursor-pointer"
                      onClick={addRow}
                    >
                      <PlusIcon />
                    </div>
                  )}
                </div>
              </div>
              {rowsToRender.length > 1 && (
                <div className="px-5 pt-4 pb-5">
                  <hr className="bg-white/40" />
                </div>
              )}
            </Fragment>
          );
        })}
      </form>
    </RHFieldWrapper>
  );
}
